import { useNavigate } from '@solidjs/router'
import { createQuery, useQueryClient } from '@tanstack/solid-query'
import type { InboxNewItem, InboxState } from '@veem/crm-schema'
import { showToast } from '@veem/ui'
import { createEffect, createMemo, createRoot, createSignal, on, onCleanup } from 'solid-js'
import { api } from '../../lib/api.ts'
import { refreshPush, registerServiceWorker } from '../../lib/push.ts'
import { queryKeys } from '../../lib/query-keys.ts'
import { formatCount } from '../lib/format.ts'
import { inboxIds } from './inbox.ts'
import { proto } from './store.ts'

const inboxKey = (personId: string) => [...queryKeys.module('tasks'), 'inbox', personId] as const

const [state, setState] = createSignal<InboxState | null>(null)
const [seenLocally, setSeenLocally] = createSignal<ReadonlySet<string>>(new Set())

const newIds = createRoot(() => {
  const ids = createMemo(() => {
    const local = seenLocally()
    return new Set((state()?.items ?? []).map((item) => item.id).filter((id) => !local.has(id)))
  })
  return ids
})

export const isNew = (id: string) => newIds().has(id)
export const pushKey = () => state()?.pushKey ?? null

/** New items that are still in the person's queue on screen, for the badges. */
export const newInboxCount = () =>
  inboxIds(proto.role(), proto.personId()).filter((id) => newIds().has(id)).length

export const markSeen = (id: string) => {
  if (!newIds().has(id)) return
  setSeenLocally((current) => new Set([...current, id]))
  void api('/inbox/seen', { method: 'POST', body: JSON.stringify({ ids: [id] }) }).catch(() => {
    setSeenLocally((current) => new Set([...current].filter((entry) => entry !== id)))
  })
}

const announce = (items: InboxNewItem[], open: (href: string) => void) => {
  const [first] = items
  if (!first) return
  if (items.length === 1)
    showToast(`Нове завдання: ${first.title}`, 'success', {
      label: 'Відкрити',
      onClick: () => open(first.href),
    })
  else
    showToast(`Нові завдання: ${formatCount(items.length)}`, 'success', {
      label: 'Переглянути',
      onClick: () => open('/prototype/inbox'),
    })
}

/**
 * Keeps "нове" in sync with the server. Mounted once inside the router. SSE invalidates the
 * query whenever the person's queue changes on any device; items that appear while the app
 * is open also get a toast.
 */
export const useInboxNews = () => {
  const navigate = useNavigate()
  const client = useQueryClient()
  const query = createQuery(() => ({
    queryKey: inboxKey(proto.personId()),
    queryFn: () => api<InboxState>('/inbox'),
    staleTime: 15_000,
  }))
  let announced: Set<string> | null = null
  createEffect(
    on(
      () => proto.personId(),
      () => {
        announced = null
        setState(null)
        setSeenLocally(new Set<string>())
      },
    ),
  )
  createEffect(() => {
    const data = query.data
    if (!data) return
    const fresh = announced ? data.items.filter((item) => !announced?.has(item.id)) : []
    announced = new Set(data.items.map((item) => item.id))
    setState(data)
    const live = new Set(data.items.map((item) => item.id))
    setSeenLocally((current) => new Set([...current].filter((id) => live.has(id))))
    announce(fresh, (href) => navigate(href))
  })
  createEffect(
    on(pushKey, (key) => {
      void refreshPush(key).catch(() => {})
    }),
  )
  void registerServiceWorker()
  if ('serviceWorker' in navigator) {
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | null
      if (data?.type === 'veem-open' && data.url?.startsWith('/')) navigate(data.url)
      if (data?.type === 'veem-push')
        void client.invalidateQueries({ queryKey: inboxKey(proto.personId()) })
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    onCleanup(() => navigator.serviceWorker.removeEventListener('message', onMessage))
  }
}
